"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import {
  CircleCheck,
  Compass,
  Download,
  EllipsisVertical,
  Share,
  SquarePlus,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useInstallPrompt from "@/hooks/useInstallPrompt";

type InstallStep = { icon: LucideIcon; text: ReactNode };

const IOS_SAFARI_STEPS: InstallStep[] = [
  { icon: Share, text: <>Tap <strong>Share</strong> (□↑) in Safari&apos;s toolbar.</> },
  { icon: SquarePlus, text: <>Scroll down and tap <strong>Add to Home Screen</strong>.</> },
  { icon: CircleCheck, text: <>Tap <strong>Add</strong>. Deemcee appears on your home screen.</> },
];

// Other iOS browsers can't be relied on to offer "Add to Home Screen".
const IOS_OTHER_BROWSER_STEPS: InstallStep[] = [
  { icon: Compass, text: <>Open this page in <strong>Safari</strong>.</> },
  { icon: Share, text: <>Tap <strong>Share</strong> (□↑), then <strong>Add to Home Screen</strong>.</> },
  { icon: CircleCheck, text: <>Tap <strong>Add</strong>. Deemcee appears on your home screen.</> },
];

const BROWSER_MENU_STEPS: InstallStep[] = [
  { icon: EllipsisVertical, text: <>Open your browser menu (<strong>⋮</strong>).</> },
  { icon: Download, text: <>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</> },
  { icon: CircleCheck, text: <>Confirm with <strong>Install</strong>. Deemcee appears on your home screen.</> },
];

/**
 * Mounted once in the root layout: the menu rows close their drawer and open
 * this, so it must live outside the drawers. Renders nothing until opened.
 */
export default function InstallAppGuide() {
  const { platform, isIosSafari, guideOpen, setGuideOpen } = useInstallPrompt();

  const steps =
    platform !== "ios"
      ? BROWSER_MENU_STEPS
      : isIosSafari
        ? IOS_SAFARI_STEPS
        : IOS_OTHER_BROWSER_STEPS;

  return (
    <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3 pr-8 text-left">
            <Image
              src="/icons/deemcee-pwa-192.png"
              alt=""
              width={48}
              height={48}
              className="h-12 w-12 shrink-0 rounded-xl ring-1 ring-border"
            />
            <div className="min-w-0 space-y-1">
              <DialogTitle>Install Deemcee</DialogTitle>
              <DialogDescription>
                Add it to your home screen. It opens full screen, like an app.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ol className="flex flex-col gap-2">
          {steps.map(({ icon: Icon, text }, index) => (
            <li
              key={index}
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-3"
            >
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary-ink">
                <Icon size={20} aria-hidden />
                <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {index + 1}
                </span>
              </span>
              <p className="text-sm text-foreground">{text}</p>
            </li>
          ))}
        </ol>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" className="w-full sm:w-auto">
              Got it
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
